import { ReactNode, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useAuth } from '@/auth/useAuth'
import type { PermissionCode } from '@/auth/permissions'
import { SMB_ONLY } from '@/config/productScope'

export type HubTab = {
  key: string
  label: string
  icon?: LucideIcon
  permission?: PermissionCode
  element: ReactNode
  smbHidden?: boolean
}

type TabbedHubProps = {
  tabs: HubTab[]
  param?: string
}

export function TabbedHub({ tabs, param = 'tab' }: TabbedHubProps) {
  const { hasPermission } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const visible = useMemo(
    () => tabs.filter((t) => (!t.permission || hasPermission(t.permission)) && !(SMB_ONLY && t.smbHidden)),
    [tabs, hasPermission],
  )

  if (visible.length === 0) return null

  const requested = searchParams.get(param)
  const active = visible.some((t) => t.key === requested) ? (requested as string) : visible[0].key

  function selectTab(next: string) {
    const params = new URLSearchParams(searchParams)
    params.set(param, next)
    setSearchParams(params, { replace: true })
  }

  return (
    <Tabs value={active} onValueChange={selectTab} className="flex flex-col gap-4">
      <TabsList className="flex h-auto flex-wrap justify-start gap-1">
        {visible.map((t) => {
          const Icon = t.icon
          return (
            <TabsTrigger key={t.key} value={t.key} className="gap-2">
              {Icon && <Icon className="size-4" />}
              {t.label}
            </TabsTrigger>
          )
        })}
      </TabsList>
      {visible.map((t) => (
        <TabsContent key={t.key} value={t.key} className="mt-0">
          {t.key === active ? t.element : null}
        </TabsContent>
      ))}
    </Tabs>
  )
}
